import { zodResolver } from '@hookform/resolvers/zod';
import { type AdminSelf, isApiError } from '@saay/api-client';
import {
  formatRatePercent,
  formatTimestamp,
  parseRatePercentInput,
  rateToPercentInput,
  statusLabel,
} from '@saay/formatting';
import {
  applyFieldErrors,
  Banner,
  Button,
  CommandFeedback,
  Dialog,
  EmptyState,
  ErrorState,
  FieldErrorList,
  LoadMore,
  PercentField,
  Skeleton,
  StatusChip,
  Table,
  TextArea,
  useToast,
} from '../kit.js';
import { type CommandOutcome, useCommand } from '@saay/web-app';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';

import type {
  AdminMerchant,
  CommercialSettingVersion,
  CommercialSettingVersionCreate,
} from '../api.js';
import { SurfaceSheetSection } from '../components/surface-section.js';
import { AdminSolarDateField } from '../components/admin-date-field.js';
import { useSubmitBlocked } from '../components/form.js';
import { QueryState } from '../components/guard.js';
import { approvalSubmitLabel, canUse, executesApprovalDirectly } from '../permissions.js';
import { listStatus, useCommercialSettingVersions } from '../queries.js';
import { reasonSchema, tehranToday } from '../validation.js';
import { useRefreshMerchant } from './merchant-lifecycle.js';
import { FormReasonChips } from '../components/reason-field.js';

/**
 * Commercial settings on the merchant drawer (AC §18.4.8 M9–M10; F-5; M-32; D-61, D-66): the SAAY
 * rate versions and a new version. A manager (`APPROVAL_EXECUTE_DIRECT`) records a fee change at once
 * (`201`); anyone else still receives `202`. A future version splits the version it starts inside
 * (M-32), so nothing already recorded is dropped.
 *
 * Owner fix (D-79): the admin sets no merchant-specific installment bounds here any more. The plan
 * carries its count and interval (AC §23), and its interval is checked against the platform
 * `installment-bounds` policy, shown once on the plan section. A new version therefore sends
 * `installmentBounds: null` (platform bounds only, AC §17.5.2).
 */

function period(from: string, until: string | null) {
  return (
    <span className="saay-cell-main">
      <time dateTime={from}>{formatTimestamp(from, { withTime: false })}</time>
      <small>
        {until === null ? (
          'بدون پایان'
        ) : (
          <time dateTime={until}>{`تا ${formatTimestamp(until, { withTime: false })}`}</time>
        )}
      </small>
    </span>
  );
}

// ---------------------------------------------------------------------------------------------
// M10 createCommercialSettingVersion

const versionSchema = z
  .object({
    cashRate: z
      .string()
      .refine(
        (value) => value.trim() === '' || parseRatePercentInput(value) !== null,
        'نرخ فروش نقدی را به درصد وارد کنید، مثلاً ۲ (کمتر از ۱۰۰). خالی یعنی پیش‌فرض سیستم.',
      ),
    effectiveFrom: z.string().min(1, 'تاریخ اجرا را وارد کنید.'),
    rate: z
      .string()
      .refine(
        (value) => parseRatePercentInput(value) !== null,
        'نرخ را به درصد وارد کنید، مثلاً ۸ یا ۸٫۵ (کمتر از ۱۰۰).',
      ),
    reason: reasonSchema(),
  })
  .superRefine((value, context) => {
    if (value.effectiveFrom !== '' && value.effectiveFrom < tehranToday()) {
      context.addIssue({
        code: 'custom',
        message: 'تاریخ اجرا نمی‌تواند پیش از امروز باشد.',
        path: ['effectiveFrom'],
      });
    }
  });
export type VersionValues = z.infer<typeof versionSchema>;
const VERSION_FIELDS = ['saayFeeRate', 'cashSaleFeeRate', 'effectiveFrom', 'reason'];

/** A store's cash rate as the admin reads it (AC §34.5): `null` is no own rate, not «off». */
function cashRateText(rate: string | null): string {
  return rate === null
    ? statusLabel('cashSaleFeeSource', 'GLOBAL_DEFAULT')
    : formatRatePercent(rate, { invalid: 'raw' });
}

/** What a scan would use now (AC §34.5): the rate and whose rate it is. */
function effectiveCashFeeText(fee: NonNullable<AdminMerchant['effectiveCashSaleFee']>): string {
  return `${formatRatePercent(fee.rate, { invalid: 'raw' })} (${statusLabel('cashSaleFeeSource', fee.source)})`;
}

/**
 * The cash rate field's helper text: an empty field always means the default, whichever rate the
 * store has now.
 */
function cashRateHint(merchant: AdminMerchant): string {
  const fee = merchant.effectiveCashSaleFee;
  const empty = 'اگر خالی بگذارید، پیش‌فرض سیستم حساب می‌شود.';
  return fee === null ? empty : `نرخ فعلی ${effectiveCashFeeText(fee)}. ${empty}`;
}

/**
 * The request body (AC §18.4.8): the rate string from the typed percent. The bounds are always
 * `null` (platform bounds only, owner fix under D-79).
 */
export function toCommercialVersion(
  values: VersionValues,
  expectedVersion: number,
): CommercialSettingVersionCreate {
  const cashSaleFeeRate =
    values.cashRate.trim() === '' ? null : parseRatePercentInput(values.cashRate);
  return {
    cashSaleFeeRate,
    effectiveFrom: values.effectiveFrom,
    expectedVersion,
    installmentBounds: null,
    reason: values.reason.trim(),
    saayFeeRate: parseRatePercentInput(values.rate) ?? '',
  };
}

function VersionDialogFeedback({
  approvalId,
  command,
  onClose,
  onHandle,
  pendingId,
  sameStart,
  unmapped,
}: {
  readonly approvalId: string | undefined;
  readonly command: ReturnType<
    typeof useCommand<CommercialSettingVersionCreate, CommercialSettingVersion>
  >;
  readonly onClose: () => void;
  readonly onHandle: (outcome: CommandOutcome<CommercialSettingVersion>) => Promise<void>;
  readonly pendingId: string | undefined;
  readonly sameStart: boolean;
  readonly unmapped: ReturnType<typeof applyFieldErrors>;
}) {
  return (
    <>
      <FieldErrorList errors={unmapped} />
      <CommandFeedback
        approvalAction={
          approvalId === undefined ? undefined : (
            <Link to={`/approvals/${approvalId}`}>مشاهدهٔ درخواست تأیید</Link>
          )
        }
        onCheckStatus={() => void command.checkStatus().then(onHandle)}
        state={command.state}
      />
      {typeof pendingId === 'string' ? (
        <Banner tone="warning">
          تغییر کارمزد دیگری در انتظار تأیید است.{' '}
          <Link to={`/approvals/${pendingId}`}>مشاهدهٔ درخواست</Link>
        </Banner>
      ) : null}
      {sameStart ? (
        <Banner tone="warning">
          برای همین تاریخ نسخهٔ دیگری ثبت شده است؛ تاریخ اجرای دیگری انتخاب کنید.
        </Banner>
      ) : null}
      {command.state.status === 'approval-pending' ? (
        <Button onClick={onClose} type="button" variant="secondary">
          بستن
        </Button>
      ) : null}
    </>
  );
}

function FeeVersionDialog({
  merchant,
  onOpenChange,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenChange: (open: boolean) => void;
  readonly self: AdminSelf;
}) {
  const direct = executesApprovalDirectly(self);
  const toast = useToast();
  const refresh = useRefreshMerchant(merchant.id);
  const offline = useSubmitBlocked();
  const [unmapped, setUnmapped] = useState<ReturnType<typeof applyFieldErrors>>([]);
  const current = merchant.commercialSetting;
  const form = useForm<VersionValues>({
    defaultValues: {
      cashRate: current?.cashSaleFeeRate == null ? '' : rateToPercentInput(current.cashSaleFeeRate),
      effectiveFrom: tehranToday(),
      rate: current === null ? '' : rateToPercentInput(current.saayFeeRate),
      reason: '',
    },
    resolver: zodResolver(versionSchema),
  });
  const typedRate = parseRatePercentInput(form.watch('rate'));
  const typedCash =
    form.watch('cashRate').trim() === '' ? null : parseRatePercentInput(form.watch('cashRate'));
  const currentCash = current?.cashSaleFeeRate ?? null;
  const feeChange = current === null || (typedRate !== null && typedRate !== current.saayFeeRate);
  const cashChange = typedCash !== currentCash;
  const command = useCommand<CommercialSettingVersionCreate, CommercialSettingVersion>(
    'merchant.commercial.version',
    (client, body, intent) =>
      client.post(
        `/admin/merchants/${encodeURIComponent(merchant.id)}/commercial-setting-versions`,
        { body, intent },
      ),
    {
      onApprovalPending: () => refresh(),
      onSuccess: async (result) => {
        await refresh();
        toast.success({
          confirmedBy: result,
          description: `از ${formatTimestamp(result.data.effectiveFrom, { withTime: false })}`,
          title: 'نرخ کارمزد ثبت و اجرا شد',
        });
        onOpenChange(false);
      },
    },
  );

  async function handle(outcome: CommandOutcome<CommercialSettingVersion>): Promise<void> {
    if (outcome.status !== 'error' || !isApiError(outcome.error)) return;
    const error = outcome.error;
    const rest = applyFieldErrors(error, VERSION_FIELDS, (name, fieldError, options) => {
      const target =
        name === 'saayFeeRate' ? 'rate' : name === 'cashSaleFeeRate' ? 'cashRate' : name;
      form.setError(target as keyof VersionValues, fieldError, options);
    });
    setUnmapped(rest);
    if (
      error.code === 'RESOURCE_VERSION_CONFLICT' ||
      error.code === 'APPROVAL_REQUEST_PENDING' ||
      error.code === 'STATE_CONFLICT'
    ) {
      await refresh();
    }
  }

  const error = command.state.status === 'error' ? command.state.error : undefined;
  const pendingId =
    isApiError(error) && error.code === 'APPROVAL_REQUEST_PENDING'
      ? error.details?.approvalRequestId
      : undefined;
  const sameStart =
    isApiError(error) &&
    error.code === 'STATE_CONFLICT' &&
    typeof error.details?.commercialSettingVersionId === 'string';
  const approvalId = command.state.approval?.approvalRequestId;

  return (
    <Dialog
      eyebrow={merchant.displayNameFa}
      onOpenChange={(open) => {
        if (!open) command.reset();
        onOpenChange(open);
      }}
      open
      title="تغییر نرخ SAAY"
    >
      <form
        className="admin-form"
        noValidate
        onSubmit={form.handleSubmit(async (values) => {
          setUnmapped([]);
          await handle(await command.submit(toCommercialVersion(values, merchant.version)));
        })}
      >
        <Controller
          control={form.control}
          name="rate"
          render={({ field, fieldState }) => (
            <PercentField
              error={fieldState.error?.message}
              hint={
                current === null
                  ? undefined
                  : `نرخ فعلی ${formatRatePercent(current.saayFeeRate, { invalid: 'raw' })}`
              }
              label="نرخ SAAY"
              onBlur={field.onBlur}
              onChangeRate={(rate) => {
                field.onChange(rate === null ? '' : rateToPercentInput(rate));
              }}
              valueRate={field.value === '' ? null : parseRatePercentInput(field.value)}
            />
          )}
        />
        <Controller
          control={form.control}
          name="cashRate"
          render={({ field, fieldState }) => (
            <PercentField
              error={fieldState.error?.message}
              hint={cashRateHint(merchant)}
              label="نرخ فروش نقدی"
              onBlur={field.onBlur}
              onChangeRate={(rate) => {
                field.onChange(rate === null ? '' : rateToPercentInput(rate));
              }}
              valueRate={field.value === '' ? null : parseRatePercentInput(field.value)}
            />
          )}
        />
        <Controller
          control={form.control}
          name="effectiveFrom"
          render={({ field }) => (
            <AdminSolarDateField
              error={form.formState.errors.effectiveFrom?.message}
              label="تاریخ اجرا"
              onBlur={field.onBlur}
              onChange={field.onChange}
              value={field.value}
            />
          )}
        />
        <TextArea
          error={form.formState.errors.reason?.message}
          label="دلیل"
          maxLength={1000}
          {...form.register('reason')}
        />
        <FormReasonChips form={form} intent="merchant." />
        {feeChange ? (
          <Banner tone="info">
            {direct
              ? 'این نسخه نرخ SAAY را تعیین یا تغییر می‌دهد و بلافاصله ثبت و اجرا می‌شود.'
              : 'این نسخه نرخ SAAY را تعیین یا تغییر می‌دهد و برای تأیید ارسال می‌شود.'}
          </Banner>
        ) : null}
        {cashChange ? (
          <Banner tone="info">
            {typedCash === null
              ? 'این فروشگاه از این نسخه با پیش‌فرض سیستم حساب می‌شود.'
              : 'نرخ فروش نقدی در این نسخه ثبت می‌شود.'}
          </Banner>
        ) : null}
        <VersionDialogFeedback
          approvalId={approvalId}
          command={command}
          onClose={() => onOpenChange(false)}
          onHandle={handle}
          pendingId={typeof pendingId === 'string' ? pendingId : undefined}
          sameStart={sameStart}
          unmapped={unmapped}
        />
        {command.state.status === 'approval-pending' ? null : (
          <Button
            disabled={offline.blocked}
            loading={command.state.status === 'pending'}
            type="submit"
          >
            {feeChange || cashChange ? approvalSubmitLabel(self) : 'ثبت نسخه'}
          </Button>
        )}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------
// M9 list

function VersionHistory({ merchant }: { readonly merchant: AdminMerchant }) {
  const versions = useCommercialSettingVersions(merchant.id);
  const rows = versions.data?.pages.flatMap((page) => page.items) ?? [];
  return (
    <QueryState
      empty={<EmptyState icon="card" title="هنوز نسخه‌ای از تنظیم تجاری ثبت نشده است" />}
      error={versions.error}
      isEmpty={rows.length === 0}
      onRetry={() => void versions.refetch()}
      skeleton={<Skeleton label="در حال بارگذاری نسخه‌ها…" lines={4} />}
      status={listStatus(versions)}
    >
      <Table
        caption="نسخه‌های تنظیم تجاری"
        columns={[
          {
            header: 'دوره',
            key: 'period',
            pin: 'start',
            render: (row) => period(row.effectiveFrom, row.effectiveUntil),
          },
          {
            header: 'نرخ SAAY',
            key: 'rate',
            render: (row) => formatRatePercent(row.saayFeeRate, { invalid: 'raw' }),
          },
          {
            header: 'نرخ فروش نقدی',
            key: 'cash',
            render: (row) => cashRateText(row.cashSaleFeeRate),
          },
          { header: 'دلیل', key: 'reason', render: (row) => row.reason },
          {
            header: 'وضعیت',
            key: 'status',
            pin: 'end',
            render: (row) => (
              <span className="admin-inline">
                <StatusChip enumName="commercialSettingStatus" value={row.status} />
                {row.approvalRequestId === null ? null : (
                  <Link to={`/approvals/${row.approvalRequestId}`}>درخواست تأیید</Link>
                )}
              </span>
            ),
          },
        ]}
        regionLabel="جدول نسخه‌های تنظیم تجاری"
        rowKey={(row) => row.id}
        rows={rows}
      />
      {versions.isFetchNextPageError ? (
        <ErrorState error={versions.error} onRetry={() => void versions.fetchNextPage()} />
      ) : null}
      <LoadMore
        hasMore={versions.hasNextPage}
        loading={versions.isFetchingNextPage}
        onLoadMore={() => void versions.fetchNextPage()}
      />
    </QueryState>
  );
}

/** The «تنظیمات تجاری» section: the SAAY rate versions. */
export function CommercialSections({
  merchant,
  onOpenNewVersionHandled,
  openNewVersion,
  self,
}: {
  readonly merchant: AdminMerchant;
  readonly onOpenNewVersionHandled?: () => void;
  readonly openNewVersion?: 'fee' | false;
  readonly self: AdminSelf;
}) {
  const [open, setOpen] = useState<'fee' | false>(false);
  useEffect(() => {
    if (openNewVersion) {
      setOpen(openNewVersion);
      onOpenNewVersionHandled?.();
    }
  }, [onOpenNewVersionHandled, openNewVersion]);
  const closed = merchant.status === 'CLOSED';
  const pendingFee = merchant.pendingApprovals.some(
    (approval) => approval.actionType === 'MERCHANT_FEE_CHANGE',
  );
  const canVersion = !closed && canUse(self, 'createCommercialSettingVersion');
  return (
    <SurfaceSheetSection
      actions={
        canVersion ? (
          <Button onClick={() => setOpen('fee')} size="sm" variant="secondary">
            {merchant.commercialSetting === null ? 'ثبت نرخ' : 'تغییر نرخ'}
          </Button>
        ) : undefined
      }
      id="merchant-commercial-versions"
      title="نسخه‌های نرخ SAAY"
    >
      {merchant.effectiveCashSaleFee === null ? null : (
        <p>نرخ فروش نقدی: {effectiveCashFeeText(merchant.effectiveCashSaleFee)}</p>
      )}
      {pendingFee ? (
        <Banner tone="info">
          تغییر کارمزد این فروشگاه در انتظار تأیید است؛ پس از تأیید، نسخهٔ جدید در این فهرست دیده
          می‌شود.
        </Banner>
      ) : null}
      <VersionHistory merchant={merchant} />
      {open === 'fee' ? (
        <FeeVersionDialog
          merchant={merchant}
          onOpenChange={(next) => {
            if (!next) setOpen(false);
          }}
          self={self}
        />
      ) : null}
    </SurfaceSheetSection>
  );
}
